import React, {FC, useMemo} from "react";
import {DownloadOutlined, FileDoneOutlined, MailOutlined} from "@ant-design/icons";
import {Button, Space, Table, Typography} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {StatusTag} from "../../../Components/ui";
import {normalizeEnumKey} from "../../../i18n/labelHelpers";
import {formatLocalDateTime} from "../adminDateFormat";
import {
    canGenerateCertificateForRegistration,
    EMPTY_OPERATIONS_VALUE,
    operationalCertificateStatusTone
} from "../participantOperationsHelpers";
import {dateSorter, sortText, textSorter} from "../tableSorters";
import {displayValue} from "./participantDetailHelpers";
import type {ParticipantCertificate, ParticipantRegistration} from "./participantDetailTypes";
import {DetailPanel, EnumTag, useEmptyText} from "./participantDetailUi";
import type {ParticipantDetailModel} from "./useParticipantDetail";

// Certificate readiness per registration and the generated certificate history.
export const CertificatesSection: FC<{model: ParticipantDetailModel}> = ({model}) => {
    const {t} = useTranslation();
    const emptyText = useEmptyText();
    const {registrations, certificates, pending, actions} = model;

    const eligibilityColumns = useMemo<TableColumnsType<ParticipantRegistration>>(() => {
        const certificateCount = (registration: ParticipantRegistration) => certificates
            .filter(certificate => certificate.registrationId === registration.id)
            .length;

        return [
            {
                key: "registration",
                title: t("registration", {defaultValue: "Registration"}),
                sorter: textSorter(registration => registration.status),
                render: (_, registration) => <EnumTag value={registration.status}/>
            },
            {
                key: "certificate",
                title: t("certificate-eligibility", {defaultValue: "Certificate eligibility"}),
                sorter: textSorter(registration => sortText(
                    canGenerateCertificateForRegistration(registration.status) ? "eligible" : "not eligible",
                    certificateCount(registration)
                )),
                render: (_, registration) => {
                    const count = certificateCount(registration);
                    const canGenerate = canGenerateCertificateForRegistration(registration.status);
                    const readinessText = count > 0
                        ? `${count} ${t("certificates", {defaultValue: "certificates"})}`
                        : canGenerate
                            ? t("certificate-event-completion-required", {
                                defaultValue: "Available after event completion"
                            })
                            : t("requires-registriran-and-completed-event", {
                                defaultValue: "Requires Registered or Confirmed status and a completed event"
                            });

                    return (
                        <Space direction="vertical" size={4}>
                            <StatusTag tone={canGenerate ? "success" : "neutral"}>
                                {canGenerate
                                    ? t("certificate-registered-status", {defaultValue: "Ready"})
                                    : t("certificate-not-eligible", {defaultValue: "Unready"})}
                            </StatusTag>
                            <Typography.Text type="secondary">{readinessText}</Typography.Text>
                            <Button
                                size="small"
                                icon={<FileDoneOutlined/>}
                                loading={pending.generatingCertificateRegistrationId === registration.id}
                                disabled={!canGenerate}
                                onClick={() => actions.generateCertificate(registration)}
                            >
                                {t("generate", {defaultValue: "Generate"})}
                            </Button>
                        </Space>
                    );
                }
            }
        ];
    }, [actions, certificates, pending.generatingCertificateRegistrationId, t]);

    const historyColumns = useMemo<TableColumnsType<ParticipantCertificate>>(() => [
        {
            key: "certificate",
            title: t("certificate", {defaultValue: "Certificate"}),
            sorter: textSorter(certificate => sortText(certificate.status, certificate.participantFullName)),
            render: (_, certificate) => (
                <Space direction="vertical" size={2}>
                    {certificate.status
                        ? <EnumTag value={certificate.status} tone={operationalCertificateStatusTone(certificate.status)}/>
                        : null}
                    <Typography.Text>{displayValue(certificate.participantFullName)}</Typography.Text>
                </Space>
            )
        },
        {
            key: "registration",
            title: t("registration-state", {defaultValue: "Registration state"}),
            sorter: textSorter(certificate => certificate.registrationStatus),
            render: (_, certificate) => certificate.registrationStatus
                ? <EnumTag value={certificate.registrationStatus}/>
                : null
        },
        {
            key: "timestamps",
            title: t("history", {defaultValue: "History"}),
            sorter: dateSorter(certificate => certificate.requestedAt),
            render: (_, certificate) => (
                <Space direction="vertical" size={2}>
                    <Typography.Text type="secondary">
                        {t("requested", {defaultValue: "Requested"})}: {formatLocalDateTime(certificate.requestedAt, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("generated", {defaultValue: "Generated"})}: {formatLocalDateTime(certificate.generatedAt, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("queued", {defaultValue: "Queued"})}: {formatLocalDateTime(certificate.emailQueuedAtUtc, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("emailed", {defaultValue: "Emailed"})}: {formatLocalDateTime(certificate.emailedAt, EMPTY_OPERATIONS_VALUE)}
                    </Typography.Text>
                    {certificate.emailFailureReason?.trim() ? (
                        <Typography.Text type="danger">{certificate.emailFailureReason}</Typography.Text>
                    ) : null}
                    <Typography.Text type="secondary">
                        {t("requested-by", {defaultValue: "Requested By"})}: {displayValue(certificate.requestedBy)}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "pdf",
            title: t("certificate-document", {defaultValue: "Certificate document"}),
            sorter: textSorter(certificate => certificate.fileId ? "ready" : "missing"),
            render: (_, certificate) => (
                <StatusTag tone={certificate.fileId ? "info" : "neutral"}>
                    {certificate.fileId
                        ? t("document-ready", {defaultValue: "Available"})
                        : t("document-unavailable", {defaultValue: "Unavailable"})}
                </StatusTag>
            )
        },
        {
            key: "actions",
            title: t("actions", {defaultValue: "Actions"}),
            render: (_, certificate) => (
                <Space size={8} wrap>
                    <Button
                        size="small"
                        icon={<DownloadOutlined/>}
                        loading={pending.downloadingCertificateId === certificate.id}
                        disabled={!certificate.fileId}
                        onClick={() => actions.downloadCertificate(certificate)}
                    >
                        {t("download", {defaultValue: "Download"})}
                    </Button>
                    {normalizeEnumKey(certificate.status) === "FAILED" ? (
                        <Button
                            size="small"
                            icon={<MailOutlined/>}
                            loading={pending.resendingCertificateId === certificate.id}
                            onClick={() => actions.resendCertificateEmail(certificate)}
                        >
                            {t("resend", {defaultValue: "Resend"})}
                        </Button>
                    ) : null}
                </Space>
            )
        }
    ], [actions, pending.downloadingCertificateId, pending.resendingCertificateId, t]);

    return (
        <DetailPanel title={t("certificates", {defaultValue: "certificates"})}>
            <Table
                size="small"
                rowKey="id"
                columns={eligibilityColumns}
                dataSource={registrations}
                pagination={false}
                scroll={{x: 520}}
                locale={{emptyText}}
            />
            <Typography.Title level={4}>
                {t("certificate-history", {defaultValue: "Certificate history"})}
            </Typography.Title>
            <Table
                size="small"
                rowKey="id"
                columns={historyColumns}
                dataSource={certificates}
                pagination={false}
                scroll={{x: 980}}
                locale={{
                    emptyText: t("no-certificates", {defaultValue: "No certificates"})
                }}
            />
        </DetailPanel>
    );
};
